import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import IdleTimeoutManager from './components/IdleTimeoutManager';
import ViewerRestrictions from './components/ViewerRestrictions';
import MaintenanceGate from './components/MaintenanceGate';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import LandingPage from './pages/LandingPage';
import SiteAdminPage from './pages/SiteAdminPage';
import FinAjanPage from './pages/FinAjanPage';
import DashboardPage from './pages/DashboardPage';
import TasksPage from './pages/TasksPage';
import TaskFormPage from './pages/TaskFormPage';
import UserManagementPage from './pages/UserManagementPage';
import CalendarPage from './pages/CalendarPage';
import GrafiklerPage from './pages/GrafiklerPage';
import SunumOlusturucuPage from './pages/SunumOlusturucuPage';
import ProfilePage from './pages/ProfilePage';
import ArchivePage from './pages/ArchivePage';
import PendingTasksPage from './pages/PendingTasksPage';
import IhalelerPage from './pages/IhalelerPage';
import OdenekPage from './pages/OdenekPage';
import HakedisPage from './pages/HakedisPage';
import SantiyeTakipPage from './pages/SantiyeTakipPage';
import BakimOnarimPage from './pages/BakimOnarimPage';
import DogrudenTeminPage from './pages/DogrudenTeminPage';
import FirmaBilgileriPage from './pages/FirmaBilgileriPage';
import EmlakKamulastirmaPage from './pages/EmlakKamulastirmaPage';
import EmlakIslemleriPage from './pages/EmlakIslemleriPage';
import KamulastirmaIslemleriPage from './pages/KamulastirmaIslemleriPage';
import KiralikTasinmazIslemleriPage from './pages/KiralikTasinmazIslemleriPage';
import IstanbulHaritaPage from './pages/IstanbulHaritaPage';
import ISYPage from './pages/ISYPage';
import PKPage from './pages/PKPage';
import { Toaster } from './components/ui/sonner';
import TaskNotificationPanel from './components/TaskNotificationPanel';
import '@/App.css';
import '@/calendar.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Admin or Assistant Route Component
const AdminOrAssistantRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.is_admin && !user.is_admin_assistant) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Finance Module Route Component (Admin or Finance Permission)
const FinanceModuleRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.is_admin && !user.can_access_finance_module && user.department !== 'yatirim_insaat') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Bakim-Onarim Module Route Component (Admin or Bakim-Onarim Permission)
const BakimOnarimRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.is_admin && !user.has_bakim_onarim_access) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <IdleTimeoutManager />
        <ViewerRestrictions />
        <MaintenanceGate />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <TasksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/new"
            element={
              <AdminOrAssistantRoute>
                <TaskFormPage />
              </AdminOrAssistantRoute>
            }
          />
          <Route
            path="/tasks/edit/:taskId"
            element={
              <AdminRoute>
                <TaskFormPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminOrAssistantRoute>
                <UserManagementPage />
              </AdminOrAssistantRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <CalendarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/grafikler"
            element={
              <ProtectedRoute>
                <GrafiklerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/grafikler/sunum-olusturucu"
            element={
              <ProtectedRoute>
                <SunumOlusturucuPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/archive"
            element={
              <ProtectedRoute>
                <ArchivePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pending-tasks"
            element={
              <AdminRoute>
                <PendingTasksPage />
              </AdminRoute>
            }
          />
          <Route
            path="/finance/ihaleler"
            element={
              <FinanceModuleRoute>
                <IhalelerPage />
              </FinanceModuleRoute>
            }
          />
          <Route
            path="/finance/odenek"
            element={
              <FinanceModuleRoute>
                <OdenekPage />
              </FinanceModuleRoute>
            }
          />
          <Route
            path="/finance/hakedis"
            element={
              <FinanceModuleRoute>
                <HakedisPage />
              </FinanceModuleRoute>
            }
          />
          <Route
            path="/finance/santiye-takip"
            element={
              <FinanceModuleRoute>
                <SantiyeTakipPage />
              </FinanceModuleRoute>
            }
          />
          <Route
            path="/bakim-onarim/odenek"
            element={
              <BakimOnarimRoute>
                <BakimOnarimPage />
              </BakimOnarimRoute>
            }
          />
          <Route
            path="/bakim-onarim/dogrudan-temin"
            element={
              <BakimOnarimRoute>
                <DogrudenTeminPage />
              </BakimOnarimRoute>
            }
          />
          <Route
            path="/bakim-onarim/firma-bilgileri"
            element={
              <BakimOnarimRoute>
                <FirmaBilgileriPage />
              </BakimOnarimRoute>
            }
          />
          <Route
            path="/emlak-kamulastirma"
            element={
              <ProtectedRoute>
                <EmlakKamulastirmaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emlak-kamulastirma/emlak-islemleri"
            element={
              <ProtectedRoute>
                <EmlakIslemleriPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emlak-kamulastirma/kamulastirma-islemleri"
            element={
              <ProtectedRoute>
                <KamulastirmaIslemleriPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emlak-kamulastirma/kiralik-tasinmaz"
            element={
              <ProtectedRoute>
                <KiralikTasinmazIslemleriPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emlak-kamulastirma/harita"
            element={
              <ProtectedRoute>
                <IstanbulHaritaPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/isy"
            element={
              <ProtectedRoute>
                <ISYPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/pk"
            element={
              <ProtectedRoute>
                <PKPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<LandingPage />} />
          <Route path="/anasayfa-eski" element={<HomePage />} />
          <Route path="/site-admin" element={<SiteAdminPage />} />
          <Route path="/finajan" element={<FinAjanPage />} />
        </Routes>
        <Toaster />
        <TaskNotificationPanel />
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
